'use client';

import { Fragment, useCallback, useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useApiMutation } from '@shared/hooks/useApiMutation';
import { useApiInfiniteQuery } from '@shared/hooks/useInfiniteQuery';

import {
  getApplication,
  getApplicationReviewsInfinite,
} from '@api/applications/general';
import { setReviewFunnyReaction, setReviewHelpfulnessReaction } from '@api/applications/reactions';

import { ApplicationScoreOverview } from '@ui/uikit/components/applications/common/ApplicationScoreOverview/ApplicationScoreOverview';
import { ApplicationValidationScore } from '@ui/uikit/components/applications/common/ApplicationValidationSummary/internals/ApplicationValidationScore/ApplicationValidationScore';
import { Activity, type ActivityHandle } from '@ui/uikit/components/common/Activity';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';

import { ApplicationUserFeedbackCarousel } from '../../ApplicationUserFeedback/ApplicationUserFeedbackCarousel';
import { FeedbackSectionButtons } from '../../ApplicationUserFeedback/FeedbackSectionButtons';
import { ApplicationReviewDetails } from '../internals/ApplicationReviewsDetails/ApplicationReviewsDetails';
import styles from './ApplicationUserReviews.module.scss';

interface ApplicationUserReviewsProps {
  applicationSlug: string;
}

const ApplicationUserReviews = ({ applicationSlug }: ApplicationUserReviewsProps) => {
  const { data: appData } = useQuery({
    queryKey: ['application', applicationSlug],
    queryFn: () => getApplication(applicationSlug),
  });

  const application = appData?.payload;

  const { data, isPending } = useApiInfiniteQuery(
    getApplicationReviewsInfinite,
    [applicationSlug],
  );

  const reviews = data?.pages[0] || [];

  const { mutate: toggleHelpfulness } = useApiMutation(
    setReviewHelpfulnessReaction,
    applicationSlug,
  );
  const { mutate: setFunny } = useApiMutation(setReviewFunnyReaction, applicationSlug);

  const { user } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const activity = useRef<ActivityHandle>(null);
  const ensureAuth = useCallback(async () => activity.current?.initiate(), []);

  const queryClient = useQueryClient();

  const handleHelpfulnessToggle = useCallback(
    async (selection: FeedbackButtonGroupSelection, id: number) => {
      await ensureAuth();
      const vote = selection === 'positive' ? 1 : selection === 'negative' ? -1 : 0;
      toggleHelpfulness({ payload: { vote }, reviewId: id, queryClient });
    },
    [ensureAuth, toggleHelpfulness, queryClient],
  );

  const handleFunnyReactionToggle = useCallback(
    async (state: 'on' | 'off', id: number) => {
      await ensureAuth();
      setFunny({ payload: { funny: state === 'on' }, reviewId: id, queryClient });
    },
    [ensureAuth, setFunny, queryClient],
  );

  if (!application) {
    return null;
  }

  const ratings = application?.ratings;
  const all = ratings?.all ?? 0;
  const averageScoreRounded = ratings?.averageScoreRounded ?? 0;
  const maxValue = ratings?.maxValue ?? 5;

  return (
    <Fragment>
      <Activity ref={activity}>
        <Activity.Step requirement={!!user} action={openAuthModal} key="signin" />
      </Activity>
      <div className={styles['container']}>
        <ApplicationScoreOverview>
          {ratings && (
            <ApplicationScoreOverview.Score>
              <ApplicationValidationScore
                value={averageScoreRounded}
                maxValue={maxValue}
                votes={all}
                entityType="Review"
              />
            </ApplicationScoreOverview.Score>
          )}
          <ApplicationScoreOverview.Controls>
            <FeedbackSectionButtons
              application={application}
              feedbackType="Review"
              getItems={getApplicationReviewsInfinite}
              onFunnyReactionToggle={handleFunnyReactionToggle}
              onHelpfulnessToggle={handleHelpfulnessToggle}
              viewAllDisabled={reviews?.length === 0}
            />
          </ApplicationScoreOverview.Controls>
          <ApplicationScoreOverview.Details>
            <ApplicationReviewDetails ratings={ratings} />
          </ApplicationScoreOverview.Details>
        </ApplicationScoreOverview>

        <ApplicationUserFeedbackCarousel
          applicationSlug={applicationSlug}
          feedbackType="Review"
          feedbackItems={reviews}
          isPending={isPending}
          onHelpfulnessToggle={handleHelpfulnessToggle}
          onFunnyReactionToggle={handleFunnyReactionToggle}
        />
      </div>
    </Fragment>
  );
};

export { ApplicationUserReviews };
